import { PropsWithChildren, useEffect, useState } from "react";

import UFOLoadHold from "@atlaskit/react-ufo/load-hold";
import Suspense from "@atlaskit/react-ufo/suspense";

import { useUserContext } from "@/components/user-context/user-context-provider";
import { useGupsStatus } from "@/templates/ondemand/signup/confirmation/confirmation-page/src/hooks/use-gups";

import { HeroCTASkeleton } from "./hero-cta-skeleton";

/**
 * User/profile/GUPS readiness for the home logged-in hero.
 * ML recommendation loading is handled only in `home-page.template` (`LoggedInHeroWithMLRecommendation`)
 * with a single `useMlBasedXflowRecommendedProduct` call to avoid duplicate hooks and hero flicker.
 */
export const LoggedInHeroContainer = ({ children }: PropsWithChildren) => {
    const {
        profile: { requestInProgress: profileRequestInProgress },
        user: { requestInProgress: userRequestInProgress }
    } = useUserContext();

    const [userProfileInProgress, setUserProfileInProgress] = useState(true);

    const { getUserProfileJobFunctionCallback } = useGupsStatus();

    useEffect(() => {
        getUserProfileJobFunctionCallback("homepage-xflow-job-function").then(
            (response) => {
                setUserProfileInProgress(response?.requestInProgress);
            }
        );
    }, [getUserProfileJobFunctionCallback]);

    const isBrowser = typeof window !== "undefined";
    if (!isBrowser) {
        return <HeroCTASkeleton />;
    }

    // Checking for both user and profile request in progress,
    // since profileRequestInProgress is initially defined as 'false' and then gets updated to true when the request is made.
    if (
        userRequestInProgress ||
        profileRequestInProgress ||
        userProfileInProgress
    ) {
        return (
            <UFOLoadHold
                name={"hero-cta-skeleton"}
                hold={
                    userRequestInProgress ||
                    profileRequestInProgress ||
                    userProfileInProgress
                }
            >
                <HeroCTASkeleton />
            </UFOLoadHold>
        );
    }

    return (
        <Suspense
            interactionName={"loggedin-hero-container"}
            fallback={<HeroCTASkeleton />}
        >
            {children}
        </Suspense>
    );
};

LoggedInHeroContainer.displayName = "LoggedInHeroContainer";
